import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { LifeBuoy, Upload, X, Image as ImageIcon, CheckCircle2, Clock } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button, Input, Card, Badge, Spinner } from "@/components/kit";

const CATS = [["umum", "Umum"], ["teknis", "Masalah Teknis"], ["billing", "Pembayaran"], ["scraper", "Scraper"], ["blast", "Blast"]];
const STATUS_COLOR = { open: "amber", resolved: "green", closed: "slate" };

export default function Bantuan() {
  const [form, setForm] = useState({ subject: "", message: "", category: "umum" });
  const [image, setImage] = useState("");
  const [sending, setSending] = useState(false);
  const [list, setList] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/support/complaints").then((r) => setList(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Gambar maks 3MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.subject.trim().length < 3 || form.message.trim().length < 5) { toast.error("Lengkapi judul & pesan"); return; }
    setSending(true);
    try {
      await api.post("/support/complaints", { ...form, image_base64: image || null });
      toast.success("Pengaduan terkirim! Tim kami akan menindaklanjuti.");
      setForm({ subject: "", message: "", category: "umum" }); setImage("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-sky-50 grid place-items-center"><LifeBuoy className="w-6 h-6 text-sky-600" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold">Pusat Bantuan & Pengaduan</h1>
          <p className="text-slate-500 mt-0.5 text-sm">Ada kendala? Kirim pengaduan beserta screenshot sebagai bukti.</p>
        </div>
      </div>

      <Card className="p-6">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <Input data-testid="complaint-subject" placeholder="Judul singkat masalah" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            <select data-testid="complaint-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white">
              {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <textarea data-testid="complaint-message" placeholder="Jelaskan masalahnya..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4}
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none" />
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" id="evidence" data-testid="complaint-image" />
            {image ? (
              <div className="flex items-center gap-3">
                <img src={image} alt="bukti" className="h-16 w-16 object-cover rounded-lg border border-slate-200" />
                <button type="button" onClick={() => { setImage(""); if (fileRef.current) fileRef.current.value = ""; }} className="text-sm text-rose-500 flex items-center gap-1"><X className="w-4 h-4" /> Hapus gambar</button>
              </div>
            ) : (
              <label htmlFor="evidence" className="inline-flex items-center gap-2 text-sm font-medium text-sky-600 cursor-pointer hover:underline">
                <Upload className="w-4 h-4" /> Lampirkan screenshot (opsional, maks 3MB)
              </label>
            )}
          </div>
          <Button data-testid="complaint-submit" type="submit" loading={sending}>Kirim Pengaduan</Button>
        </form>
      </Card>

      <div>
        <h2 className="font-display font-bold text-lg mb-3">Riwayat Pengaduan</h2>
        {list === null ? <Spinner className="w-6 h-6" /> :
          list.length === 0 ? <Card className="p-8 text-center text-slate-400">Belum ada pengaduan.</Card> : (
            <div className="space-y-3">
              {list.map((c) => (
                <Card key={c.id} className="p-4" data-testid="complaint-history-row">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800">{c.subject}</h3>
                    <Badge color={STATUS_COLOR[c.status] || "slate"}>
                      {c.status === "open" ? <><Clock className="w-3 h-3" /> Diproses</> : <><CheckCircle2 className="w-3 h-3" /> {c.status}</>}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{c.message}</p>
                  {c.admin_reply && <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg p-2 mt-2"><b>Balasan admin:</b> {c.admin_reply}</p>}
                </Card>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
